import { useState } from 'react';
import { Link } from 'react-router';
import { patchApi } from '../api/client';
import { shortWhen } from '../lib/format';
import { useData } from '../lib/useData';
import { Button, Card, Chip, LoadError, Loading, Notice } from './ui';

/**
 * Stitch Admin → System: Patches (quick app-only calls on LiveKit). Whether they're switched on,
 * whether LiveKit answers, and whether its webhooks are arriving. Customers never see any of
 * this: with Patches off, Messages simply has no "Patch in".
 */
export function PatchesCard() {
  const st = useData(() => patchApi.status(), [], { live: false });
  if (st.error) {
    return (
      <Card title="Patches">
        <LoadError error={st.error} retry={st.reload} />
      </Card>
    );
  }
  const s = st.data;
  if (!s) {
    return (
      <Card title="Patches">
        <Loading />
      </Card>
    );
  }
  if (!s.configured) {
    return (
      <Card title="Patches">
        <div className="card-body">
          <dl className="dl">
            <dt>Status</dt>
            <dd>
              <Chip>Off</Chip>
            </dd>
          </dl>
        </div>
        <div className="card-foot">
          <span className="muted" style={{ fontSize: 13 }}>
            Patches are off, so Messages has no “Patch in”. Set LIVEKIT_URL, LIVEKIT_API_KEY and LIVEKIT_API_SECRET (and LIVEKIT_API_URL inside EasyPanel) on the server, then redeploy. See docs/patches.md.
          </span>
        </div>
      </Card>
    );
  }
  return (
    <Card title="Patches">
      <div className="card-body">
        <dl className="dl">
          <dt>LiveKit</dt>
          <dd>
            {s.livekit.ok ? <Chip tone="ok">Answering</Chip> : <Chip tone="crit">Not answering</Chip>}
            <div className="mono" style={{ overflowWrap: 'anywhere', marginTop: 4 }}>
              {s.url}
            </div>
            {s.apiUrl !== s.url.replace(/^ws/, 'http') && (
              <div className="mono muted" style={{ overflowWrap: 'anywhere', marginTop: 2, fontSize: 12.5 }}>
                API: {s.apiUrl}
              </div>
            )}
            {!s.livekit.ok && s.livekit.error && <div className="muted" style={{ marginTop: 4, fontSize: 13 }}>{s.livekit.error}</div>}
          </dd>
          <dt>Webhooks</dt>
          <dd>
            {s.webhooks.inPortal ? (
              // Stitch Admin in its own container: LiveKit's webhooks (and the check against LiveKit) are the portal's.
              <Chip>Go to the portal’s container</Chip>
            ) : s.webhooks.lastAt ? (
              <>
                <Chip tone="ok">Arriving</Chip> <span className="muted">last {shortWhen(s.webhooks.lastAt)}</span>
              </>
            ) : (
              <Chip tone="warn">None since the last restart</Chip>
            )}
          </dd>
          <dt>Going now</dt>
          <dd>{s.live === 1 ? '1 Patch' : `${s.live} Patches`}</dd>
          <dt>Notifications</dt>
          <dd>{s.push ? <Chip tone="ok">On</Chip> : <Chip>Not set up</Chip>}</dd>
        </dl>
      </div>
      {!s.webhooks.lastAt && !s.webhooks.inPortal && (
        <div className="card-foot">
          <span className="muted" style={{ fontSize: 13 }}>
            Patches work without webhooks (Stitch asks LiveKit every 15 seconds), but who’s in updates faster with them. Point LiveKit’s webhook at /api/livekit/webhook with the same API key. See docs/patches.md.
          </span>
        </div>
      )}
    </Card>
  );
}

/**
 * Stitch Admin → System → Patch rings (1.12.18): each company's hidden numbers that let a Patch
 * ring desk phones (66<ext> per person) and light busy lamps (the presence room). Counts against
 * the people there, numbers someone else already uses (skipped), the daily check, and "Make Patch
 * rings": the catch-up change set for people who were there before (in Change history, undoable).
 */
export function PatchRingsCard() {
  const st = useData(() => patchApi.rings(), [], { live: false });
  const [busy, setBusy] = useState<string | null>(null);
  const [said, setSaid] = useState<{ tone: 'ok' | 'crit'; text: string } | null>(null);
  async function run(key: string, fn: () => Promise<string>) {
    setBusy(key);
    setSaid(null);
    try {
      setSaid({ tone: 'ok', text: await fn() });
      st.reload();
    } catch (e) {
      setSaid({ tone: 'crit', text: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(null);
    }
  }
  const check = () =>
    run('check', async () => {
      const r = await patchApi.checkRings();
      return r.lastRun ? `Checked ${r.lastRun.tenants} ${r.lastRun.tenants === 1 ? 'company' : 'companies'}; removed ${r.lastRun.removed}.` : 'Checked.';
    });
  if (st.error) {
    return (
      <Card title="Patch rings">
        <LoadError error={st.error} retry={st.reload} />
      </Card>
    );
  }
  const s = st.data;
  if (!s) {
    return (
      <Card title="Patch rings">
        <Loading />
      </Card>
    );
  }
  const old = s.gateway.features !== null && !s.gateway.features.includes('patch-calls');
  return (
    <Card title="Patch rings">
      <div className="card-body">
        <p className="muted" style={{ fontSize: 13, marginTop: 0 }}>
          Hidden numbers Stitch keeps on the phone system so a Patch can ring desk phones (66 and the extension, one per person) and people in a Patch show busy (the presence room, 66000). Nobody sees them in Stitch. They follow people in every change set; the
          daily check removes Stitch’s own whose person is gone.
        </p>
        {s.gateway.features === null && <Notice tone="warn">The call gateway isn’t answering, so Patches don’t ring desk phones right now.</Notice>}
        {old && <Notice tone="warn">The call gateway needs 1.12.18 or later before Patches can ring desk phones (it doesn’t offer Patch calls yet).</Notice>}
        {said && <Notice tone={said.tone === 'ok' ? 'ok' : 'crit'}>{said.text}</Notice>}
        {s.companies.length === 0 ? (
          <p className="muted">
            No company has App calling on, so none has Patch rings. Turn it on per customer in <Link to="/admin/updates/app-calling">Updates</Link>.
          </p>
        ) : (
          <div className="table-wrap" style={{ marginTop: 12 }}>
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">Company</th>
                  <th scope="col">Patch rings</th>
                  <th scope="col" className="hide-sm">
                    Presence room
                  </th>
                  <th scope="col" className="hide-sm">
                    Last check
                  </th>
                  <th scope="col">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {s.companies.map((c) => {
                  const needed = (c.missing ?? 0) > 0 || !c.presenceRoom;
                  return (
                    <tr key={c.tenant}>
                      <td>
                        <span className="cell-stack">
                          <strong style={{ fontWeight: 650 }}>{c.name}</strong>
                          <small className="mono">{c.tenant}</small>
                          {!c.available.ok && <small className="muted">{c.available.reason}</small>}
                        </span>
                      </td>
                      <td>
                        <span className="cell-stack">
                          <span>
                            {c.ringsThere ?? c.rings} of {c.people} {c.people === 1 ? 'person' : 'people'}
                          </span>
                          {(c.missing ?? 0) > 0 && <small className="muted">{c.missing} without one yet</small>}
                          {c.ringsThere !== null && c.ringsThere < c.rings && <small className="muted">{c.rings - c.ringsThere} gone from the phone system</small>}
                          {c.clashes.map((x) => (
                            <small key={x.number} className="muted">
                              {x.number} is already {x.what}: skipped{x.extension ? ` (no Patch ring for ${x.extension})` : ''}
                            </small>
                          ))}
                        </span>
                      </td>
                      <td className="hide-sm">
                        {c.presenceRoom ? c.presenceRoom.there === false ? <Chip tone="warn">{c.presenceRoom.number} missing</Chip> : <Chip tone="ok">{c.presenceRoom.number}</Chip> : <span className="muted">–</span>}
                      </td>
                      <td className="hide-sm">
                        {c.lastCheck ? (
                          <span className="cell-stack">
                            <span>{shortWhen(c.lastCheck.checkedAt)}</span>
                            {c.lastCheck.removed > 0 && <small className="muted">removed {c.lastCheck.removed}</small>}
                            {c.lastCheck.error && <small className="muted">{c.lastCheck.error}</small>}
                          </span>
                        ) : (
                          <span className="muted">–</span>
                        )}
                      </td>
                      <td>
                        {/* 1.12.33: made from Stitch Admin → Updates → Patch rings (or the customer's page). */}
                        {needed && c.available.ok && (
                          <Link className="btn small" to="/admin/updates/patch-rings">
                            In Updates
                          </Link>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <div className="card-foot">
        <span className="muted" style={{ fontSize: 13 }}>
          {s.lastRun ? `Daily check: ${shortWhen(s.lastRun.at)}${s.lastRun.error ? ` (${s.lastRun.error})` : ''}.` : 'The daily check hasn’t run since the last restart.'} See docs/patches.md.
        </span>
        <Button size="small" variant="ghost" disabled={busy !== null} onClick={() => void check()}>
          {busy === 'check' ? 'Checking…' : 'Check now'}
        </Button>
      </div>
    </Card>
  );
}
